

import React, { useState, useEffect ,useRef} from "react";
import { useSelector, useDispatch } from "react-redux";
import { fetchDashboardData } from "../../redux/actions/dashboardActions";
import { createOrder, updateOrder } from "../../redux/actions/orderActions";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "./DashboardLayout";
import { useLocation } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import axiosInstance from "../../api/axiosInstance";
import { toast } from "react-toastify";
import { FullScreenLoader } from "../Components/Loader";

const OrderRequest = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { userInfo } = useSelector((state) => state.auth);
  const {
    loading: orderLoading,
  } = useSelector((state) => state.order);

  // ── Breakdowns ──
  const [couponBreakdowns, setCouponBreakdowns] = useState([
    { amount: 0, quantity: 0 },
  ]);
  const [promoBreakdowns, setPromoBreakdowns] = useState([
    { amount: 0, quantity: 0, scheme: "", remarks: "" },
  ]);
  const [loyaltyBreakdowns, setLoyaltyBreakdowns] = useState([
    { quantity: 0, scheme: "", remarks: "" },
  ]);

  // ── Loyalty-specific state ──
  const [offerSchemes, setOfferSchemes] = useState([]);
  const [loadingSchemes, setLoadingSchemes] = useState(false);
  const [isAutoActivate, setIsAutoActivate] = useState(true);
  const [showGiftMasterModal, setShowGiftMasterModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
const [orderName, setOrderName] = useState("");
  const [orderAmount, setOrderAmount] = useState(0);
  const [qrQuantity, setQrQuantity] = useState(0);
const lastPromoBreakdowns = useRef(null);
  const location = useLocation();
  const editMode = location.state?.editMode || false;
  const orderToEdit = location.state?.order || null;

  // ── Type mapping ──
  const typeMap = {
    cashback: "coupon",
    promotion: "promotion",
    promotional: "promotion",
    coupon: "coupon",
    loyalty: "loyalty",
    loyality: "loyalty",   // handle API typo
  };
  const initialType = typeMap[location.state?.type] || "coupon";
  const isTypeLocked = !!location.state?.type;
  const [submitType, setSubmitType] = useState(initialType);

  // ── Fetch offer schemes (loyalty only) ──
  const fetchOfferSchemes = async () => {
    if (!userInfo || !userInfo.access_token) return;
    try {
      setLoadingSchemes(true);
      const response = await axiosInstance.get("/api/get-offer-schemes/", {
        headers: { Authorization: `Bearer ${userInfo.access_token}` },
      });

      const hasNoData =
        !response.data.data ||
        (Array.isArray(response.data.data) && response.data.data.length === 0) ||
        response.data.data === null;

      const isNotFound =
        response.data.message?.toLowerCase().includes("not found") ||
        response.data.message?.toLowerCase().includes("no data") ||
        response.data.status === 404;

      if (hasNoData || isNotFound) {
        setShowGiftMasterModal(true);
        setOfferSchemes([]);
        return;
      }

      if (response.data.status === 200 && response.data.data) {
        const schemes = response.data.data;
        setOfferSchemes(schemes);
        // Auto-select if only one scheme
        if (schemes.length === 1) {
          setLoyaltyBreakdowns((prev) =>
            prev.map((b) => (!b.scheme ? { ...b, scheme: schemes[0].name } : b))
          );
        }
      }
    } catch (error) {
      console.error("Error fetching offer schemes:", error);
      if (
        error.response?.status === 404 ||
        error.response?.data?.message?.toLowerCase().includes("not found") ||
        error.response?.data?.message?.toLowerCase().includes("no data")
      ) {
        setShowGiftMasterModal(true);
        setOfferSchemes([]);
      }
    } finally {
      setLoadingSchemes(false);
    }
  };

  useEffect(() => {
    if (!userInfo || !userInfo.access_token) {
      navigate("/login");
    } else {
      dispatch(fetchDashboardData(userInfo.access_token));
      if (submitType === "loyalty") {
        fetchOfferSchemes();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo, navigate]);

  // Fetch schemes when switching to loyalty tab
  useEffect(() => {
    if (submitType === "loyalty" && offerSchemes.length === 0 && !loadingSchemes) {
      fetchOfferSchemes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitType]);

  // ── Active breakdowns ──
  const activeBreakdowns =
    submitType === "coupon"
      ? couponBreakdowns
      : submitType === "promotion"
      ? promoBreakdowns
      : loyaltyBreakdowns;

  const isReviewReady = activeBreakdowns.some((b) => b.quantity > 0);

  // ── Recalculate totals ──
  useEffect(() => {
    const active =
      submitType === "coupon"
        ? couponBreakdowns
        : submitType === "promotion"
        ? promoBreakdowns
        : loyaltyBreakdowns;

    const totalAmount = active.reduce(
      (sum, b) => sum + (parseInt(b.amount) || 0) * (parseInt(b.quantity) || 0),
      0
    );
    const totalQuantity = active.reduce(
      (sum, b) => sum + (parseInt(b.quantity) || 0),
      0
    );
    setOrderAmount(totalAmount);
    setQrQuantity(totalQuantity);
  }, [couponBreakdowns, promoBreakdowns, loyaltyBreakdowns, submitType]);

  // ── Breakdown handlers ──
  const handleBreakdownChange = (index, field, value) => {
    const data =
      submitType === "coupon"
        ? [...couponBreakdowns]
        : submitType === "promotion"
        ? [...promoBreakdowns]
        : [...loyaltyBreakdowns];

    if (field === "amount" || field === "quantity") {
      if (/^\d{0,10}$/.test(value)) {
        data[index][field] = value === "" ? "" : parseInt(value, 10);
      }
    } else {
      data[index][field] = value;
    }

    if (submitType === "coupon") setCouponBreakdowns(data);
    else if (submitType === "promotion") setPromoBreakdowns(data);
    else setLoyaltyBreakdowns(data);
  };

  const addBreakdown = () => {
    if (submitType === "coupon") {
      setCouponBreakdowns([...couponBreakdowns, { amount: 0, quantity: 0 }]);
    } else if (submitType === "promotion") {
      setPromoBreakdowns([
        ...promoBreakdowns,
        { amount: 0, quantity: 0, scheme: "", remarks: "" },
      ]);
    } else {
      const defaultScheme = offerSchemes.length === 1 ? offerSchemes[0].name : "";
      setLoyaltyBreakdowns([
        ...loyaltyBreakdowns,
        { quantity: 0, scheme: defaultScheme, remarks: "" },
      ]);
    }
  };

  const removeBreakdown = (index) => {
    if (submitType === "coupon") {
      const updated = [...couponBreakdowns];
      updated.splice(index, 1);
      setCouponBreakdowns(updated);
    } else if (submitType === "promotion") {
      const updated = [...promoBreakdowns];
      updated.splice(index, 1);
      setPromoBreakdowns(updated);
    } else {
      const updated = [...loyaltyBreakdowns];
      updated.splice(index, 1);
      setLoyaltyBreakdowns(updated);
    }
  };

  // ── Submit ──
  const handleSubmit = async (e, type) => {
    e.preventDefault();

    // Loyalty uses its own dedicated API
    if (type === "loyalty") {
      for (let row of loyaltyBreakdowns) {
        if (!row.scheme || !row.scheme.trim()) {
          alert("Scheme name cannot be empty or just spaces");
          return;
        }
        if (!row.quantity || row.quantity <= 0) {
          alert("QR Quantity must be greater than 0");
          return;
        }
      }

      const payload = {
        breakdowns: loyaltyBreakdowns.map((b) => ({
          quantity: b.quantity,
          scheme: b.scheme,
          remarks: b.remarks || "",
          amount: b.amount || 0,
        })),
        is_auto_activate: isAutoActivate,
      };

      setIsSubmitting(true);
      try {
        const response = await axiosInstance.post(
          "/api/create-order-with-activation-and-gifts/",
          payload,
          {
            headers: {
              Authorization: `Bearer ${userInfo.access_token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (response.data.status === 201) {
          toast.success(response.data.message || "Order created successfully");
          if (response.data.data?.gift_allotments?.length > 0) {
            toast.info(
              `${response.data.data.total_gift_allotments} gift(s) automatically allocated based on scheme names`
            );
          }
          navigate("/dashboard/orders");
        } else {
          toast.error(response.data.message || "Failed to create order");
        }
      } catch (error) {
        console.error("Error creating order:", error);
        toast.error(
          error.response?.data?.message ||
            "An error occurred while creating the order"
        );
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    // Cashback / Promotional — uses Redux actions
    const payload = {
      order_type: type === "coupon" ? "cashback" : "promotional",
       order_remarks: orderName,
    };

    if (type === "coupon") {
      
      payload.order_amount = orderAmount;
      payload.qr_quantity = qrQuantity;
      payload.breakdowns = couponBreakdowns;
    } else if (type === "promotion") {
       lastPromoBreakdowns.current = [...promoBreakdowns];
      payload.order_remarks = promoBreakdowns[0]?.scheme || "";
      payload.order_amount = orderAmount;
      payload.qr_quantity = qrQuantity;
      payload.breakdowns = promoBreakdowns.map((b) => ({
        amount: b.amount || null,
        quantity: b.quantity,
        // scheme: b.scheme,
        // remarks: b.remarks,
      }));
    }

    if (editMode) {
      payload.order_id = orderToEdit.order_id;
      const result = await dispatch(updateOrder(payload, userInfo.access_token));
      if (result?.success) navigate("/dashboard/orders");
    } else {
      const result = await dispatch(createOrder(payload, userInfo.access_token));
      if (result?.success) navigate("/dashboard/orders");
    }
  };

  // ── Edit mode prefill ──
  useEffect(() => {
    if (editMode && orderToEdit) {
      setOrderAmount(orderToEdit.order_amount);
      setQrQuantity(orderToEdit.qr_quantity);
      const type = orderToEdit.order_type;
      if (type === "cashback") {
        setSubmitType("coupon");
        setCouponBreakdowns(orderToEdit.breakdowns || []);
      } else if (type === "promotional") {
        setSubmitType("promotion");
        // setPromoBreakdowns(orderToEdit.breakdowns || []);
        setPromoBreakdowns(
    orderToEdit.breakdowns?.length > 0
      ? orderToEdit.breakdowns.map((b) => ({
          amount: b.amount || 0,
          quantity: b.quantity || 0,
          scheme: orderToEdit.order_remarks || "",
          remarks: b.remarks || "",
        }))
      : [{ amount: 0, quantity: 0, scheme: "", remarks: "" }]
  );
        
      } else if (type === "loyalty") {
        setSubmitType("loyalty");
        setLoyaltyBreakdowns(orderToEdit.breakdowns || []);
      }
    }
  }, [editMode, orderToEdit]);

  const getPageTitle = () => {
    if (submitType === "promotion") return "Promotional Order Request";
    if (submitType === "loyalty") return "Create Loyalty Coupon";
    return "Cashback Order Request";
  };

  const getSubmitLabel = () => {
    if (orderLoading) return "Submitting...";
    if (submitType === "coupon") return "Submit Cashback";
    if (submitType === "promotion") return "Submit Promotional";
    return "Submit Loyalty Coupon";
  };

  return (
    <DashboardLayout>
      <FullScreenLoader
        show={isSubmitting || orderLoading || loadingSchemes}
        message={
          isSubmitting
            ? "Creating order, please wait..."
            : orderLoading
            ? "Submitting order..."
            : "Loading schemes..."
        }
        variant="primary"
        blurIntensity="md"
      />

      {/* ── Gift Masters Modal (loyalty only) ── */}
      {showGiftMasterModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 border-2 border-amber-500">
            <div className="flex items-center justify-center mb-4">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center">
                <span className="text-3xl">🎁</span>
              </div>
            </div>
            <h3 className="text-xl font-bold text-gray-900 text-center mb-3">
              Gift Masters Required
            </h3>
            <p className="text-gray-700 text-center mb-6">
              No gift schemes found. Please create gift masters first before
              creating loyalty coupon orders.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowGiftMasterModal(false)}
                className="flex-1 bg-gray-200 text-gray-700 py-2.5 px-4 rounded-lg hover:bg-gray-300 font-semibold transition-all duration-200"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowGiftMasterModal(false);
                  navigate("/dashboard/giftList");
                }}
                className="flex-1 bg-gradient-to-r from-amber-500 to-amber-600 text-white py-2.5 px-4 rounded-lg hover:from-amber-600 hover:to-amber-700 font-semibold transition-all duration-200 shadow-md hover:shadow-lg"
              >
                Go to Gift Masters
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="relative pt-4 px-4">
        {/* ── Header ── */}
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => navigate(-1)}
            className="hover:text-gray-800 transition-colors p-2 hover:bg-amber-50 rounded-lg"
          >
            <FaArrowLeft size={20} />
          </button>
          <h2 className="text-2xl font-bold bg-gradient-to-r from-amber-600 to-amber-500 bg-clip-text text-transparent">
            {getPageTitle()}
          </h2>
        </div>

        {/* ── Toggle buttons (hidden when type is locked from navigation) ── */}
        {!isTypeLocked && (
          <div className="flex gap-4 mb-6">
            <button
              type="button"
              onClick={() => setSubmitType("coupon")}
              className={`py-2 px-4 rounded-lg font-semibold transition ${
                submitType === "coupon"
                  ? "bg-amber-600 text-white shadow-md"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
            >
              Create Cashback Order
            </button>
            <button
              type="button"
              onClick={() => setSubmitType("promotion")}
              className={`py-2 px-4 rounded-lg font-semibold transition ${
                submitType === "promotion"
                  ? "bg-amber-600 text-white shadow-md"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
            >
              Create Promotional Order
            </button>
            <button
              type="button"
              onClick={() => setSubmitType("loyalty")}
              className={`py-2 px-4 rounded-lg font-semibold transition ${
                submitType === "loyalty"
                  ? "bg-amber-600 text-white shadow-md"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
            >
              Create Loyalty Order
            </button>
          </div>
        )}

        {/* ══════════════════════════════════════
            CASHBACK & PROMOTIONAL  (original UI)
        ══════════════════════════════════════ */}
        {(submitType === "coupon" || submitType === "promotion") && (
          <form
            onSubmit={handleSubmit}
            className="bg-white border border-gray-200 p-8 rounded-xl shadow-sm space-y-8"
          >
            <div>
              {/* ── CASHBACK ── */}
              {submitType === "coupon" && (
                <>
                 <div className="mb-4">
      <label className="block text-lg font-semibold text-gray-950 mb-2">
        Order Name
      </label>
      <input
        type="text"
        placeholder="Enter order name"
        value={orderName}
        onChange={(e) => setOrderName(e.target.value)}
        className="w-full border border-gray-950 p-3 rounded-lg focus:outline-none"
      />
    </div>
                  <label className="block text-lg font-semibold text-gray-950 mb-4">
                    Denominations
                  </label>
                  
                  {activeBreakdowns.map((b, i) => (
                    <div key={i} className="flex items-center gap-4 mb-3">
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="\d*"
                        placeholder="Amount"
                        value={b.amount || ""}
                        maxLength={10}
                        onChange={(e) =>
                          handleBreakdownChange(i, "amount", e.target.value)
                        }
                        className="w-1/2 border border-gray-950 p-3 rounded-lg focus:outline-none"
                      />
                      <input
                        type="number"
                        placeholder="QR Quantity"
                        value={b.quantity || ""}
                        onChange={(e) =>
                          handleBreakdownChange(i, "quantity", e.target.value)
                        }
                        onWheel={(e) => e.target.blur()} 
                        className="w-1/2 border border-gray-950 p-3 rounded-lg focus:outline-none"
                      />
                      <button
                        type="button"
                        onClick={() => removeBreakdown(i)}
                        className="ml-2 text-red-500 hover:text-red-700 text-xl font-bold"
                      >
                        &times;
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addBreakdown}
                    className="mt-2 text-sm text-amber-600 hover:underline"
                  >
                    + Add More Denomination
                  </button>
                </>
              )}

              {/* ── PROMOTIONAL ── */}
              {/* {submitType === "promotion" && (
                <>
                  <label className="block text-lg font-semibold text-gray-950 mb-4">
                    Promotional QR Quantity
                  </label>
                  {activeBreakdowns.map((b, i) => (
                    <div
                      key={i}
                      className="grid grid-cols-[1fr_1fr_1fr_1fr_auto] gap-4 mb-3 items-center"
                    >
                      <input
                        type="number"
                        placeholder="QR Quantity"
                        
                        value={b.quantity || ""}
                        onChange={(e) =>
                          handleBreakdownChange(i, "quantity", e.target.value)
                        }
                        onWheel={(e) => e.target.blur()} 
                        className="border border-gray-300 p-3 rounded-lg focus:outline-none"
                      />
                      <input
                        type="number"
                        placeholder="Budget (Optional)"
                        value={b.amount || ""}
                        onChange={(e) =>
                          handleBreakdownChange(i, "amount", e.target.value)
                        }
                        className="border border-gray-300 p-3 rounded-lg focus:outline-none"
                      />
                      <input
                        type="text"
                        required
                        placeholder="Scheme Name"
                        pattern=".*\S.*"
                        title="Scheme name cannot be empty or only spaces"
                        value={b.scheme || ""}
                        onChange={(e) =>
                          handleBreakdownChange(i, "scheme", e.target.value)
                        }
                        onBlur={(e) => {
                          if (!e.target.value.trim())
                            alert("Scheme name cannot be empty or just spaces");
                        }}
                        className="border border-gray-300 p-3 rounded-lg focus:outline-none"
                      />
                      <input
                        type="text"
                        placeholder="Remarks"
                        value={b.remarks || ""}
                        onChange={(e) =>
                          handleBreakdownChange(i, "remarks", e.target.value)
                        }
                        className="border border-gray-300 p-3 rounded-lg focus:outline-none"
                      />
                      <button
                        type="button"
                        onClick={() => removeBreakdown(i)}
                        className="text-red-500 text-xl font-bold hover:text-red-700"
                      >
                        &times;
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addBreakdown}
                    className="mt-2 text-sm text-amber-600 hover:underline"
                  >
                    + Add More Promotional QR Quantity
                  </button>
                </>
              )} */}

              {submitType === "promotion" && (
                <>
                  {/* Common Scheme Name — like Order Name in cashback */}
                  <div className="mb-4">
                    <label className="block text-lg font-semibold text-gray-950 mb-2">
                      Scheme Name
                    </label>
                    <input
                      type="text"
                      placeholder="Enter scheme name"
                      value={promoBreakdowns[0]?.scheme || ""}
                      onChange={(e) => {
                        const updated = promoBreakdowns.map((b) => ({ ...b, scheme: e.target.value }));
                        setPromoBreakdowns(updated);
                      }}
                      required
                      className="w-full border border-gray-950 p-3 rounded-lg focus:outline-none"
                    />
                  </div>

                  <label className="block text-lg font-semibold text-gray-950 mb-4">
                    Promotional QR Details
                  </label>

                  {activeBreakdowns.map((b, i) => (
                    <div key={i} className="flex items-center gap-4 mb-3">
                     
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="\d*"
                        placeholder="QR Quantity"
                        value={b.quantity || ""}
                        maxLength={10}
                        onChange={(e) => handleBreakdownChange(i, "quantity", e.target.value)}
                        className="w-1/2 border border-gray-950 p-3 rounded-lg focus:outline-none"
                      />
                       <input
                        type="text"
                        inputMode="numeric"
                        pattern="\d*"
                        placeholder="Points"
                        value={b.amount || ""}
                        maxLength={10}
                        onChange={(e) => handleBreakdownChange(i, "amount", e.target.value)}
                        className="w-1/2 border border-gray-950 p-3 rounded-lg focus:outline-none"
                      />
                      <button
                        type="button"
                        onClick={() => removeBreakdown(i)}
                        className="ml-2 text-red-500 hover:text-red-700 text-xl font-bold"
                      >
                        &times;
                      </button>
                    </div>
                  ))}

                  <button
                    type="button"
                    onClick={addBreakdown}
                    className="mt-2 text-sm text-amber-600 hover:underline"
                  >
                    + Add More
                  </button>
                </>
              )}
            </div>

            {/* Summary table — cashback & promotional */}
            {isReviewReady && (
              <div className="bg-gray-50 border p-4 rounded-md mt-6 border-amber-500">
                <h3 className="text-md font-semibold mb-4 text-gray-950">
                  Review Order Summary
                </h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full bg-white border border-amber-500 rounded-md">
                    <thead>
                      <tr className="bg-amber-100 text-gray-950 text-left text-sm">
                        {submitType === "coupon" ? (
                          <>
                            <th className="py-2 px-4 border-b">Denomination (₹)</th>
                            <th className="py-2 px-4 border-b">Quantity</th>
                            <th className="py-2 px-4 border-b">Total (₹)</th>
                          </>
                        ) : (
                          <>
                            <th className="py-2 px-4 border-b">Points</th>
                            <th className="py-2 px-4 border-b">Quantity</th>
                            <th className="py-2 px-4 border-b">Total Points</th>
                          </>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {submitType === "coupon" &&
                        activeBreakdowns
                          .filter((b) => b.amount > 0 && b.quantity > 0)
                          .map((b, i) => (
                            <tr key={i} className="text-gray-800 text-sm hover:bg-amber-100">
                              <td className="py-2 px-4 border-b">{b.amount}</td>
                              <td className="py-2 px-4 border-b">{b.quantity}</td>
                              <td className="py-2 px-4 border-b">{b.amount * b.quantity}</td>
                            </tr>
                          ))}
                      {submitType === "promotion" &&
                        activeBreakdowns
                          .filter((b) => b.quantity > 0)
                          .map((b, i) => (
                            <tr key={i} className="text-gray-800 text-sm hover:bg-amber-100">
                              <td className="py-2 px-4 border-b">{b.amount || "-"}</td>
                              <td className="py-2 px-4 border-b">{b.quantity}</td>
                              <td className="py-2 px-4 border-b">
                                {(parseInt(b.amount) || 0) * (parseInt(b.quantity) || 0)}
                              </td>
                            </tr>
                          ))}
                    </tbody>
                    {/* <tfoot>
                      <tr className="bg-amber-500 font-semibold text-gray-950 text-sm">
                        <td className="py-2 px-4 border-t">Total</td>
                        <td className="py-2 px-4 border-t">{qrQuantity}</td>
                         <td className="py-2 px-4 border-t">
                          {orderAmount}
                        </td>
                        <td className="py-2 px-4 border-t">
                          {submitType === "coupon" ? `₹${orderAmount}` : ""}
                        </td> 
                         {submitType === "promotion" && (
                          <td className="py-2 px-4 border-t"></td>
                        )} 
                      </tr>
                    </tfoot> */}
                    <tfoot>
                      <tr className="bg-amber-500 font-semibold text-gray-950 text-sm">
                        <td className="py-2 px-4 border-t">Total</td>
                        <td className="py-2 px-4 border-t">{qrQuantity}</td>
                        <td className="py-2 px-4 border-t">
                          {submitType === "coupon" ? `₹${orderAmount}` : `${orderAmount} pts`}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-4 mt-4">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="bg-white border-2 border-red-500 text-red-600 py-2 px-6 rounded-lg hover:bg-red-50 font-semibold transition-all duration-200"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={(e) => handleSubmit(e, submitType)}
                disabled={orderLoading}
                className="bg-amber-600 text-white py-2 px-6 rounded-lg hover:bg-amber-700 font-semibold transition-all duration-200 disabled:opacity-50"
              >
                {getSubmitLabel()}
              </button>
            </div>
          </form>
        )}

        {/* ══════════════════════════════════════
            LOYALTY  (new UI from latest code)
        ══════════════════════════════════════ */}
        {submitType === "loyalty" && (
          <div className="flex justify-center items-start pb-8">
            <div className="w-full max-w-4xl">
              <form
                onSubmit={(e) => handleSubmit(e, "loyalty")}
                className="bg-white border-2 border-amber-500 rounded-2xl p-6 shadow-2xl space-y-6"
              >
                {/* Order Creation Section */}
                <div className="bg-gradient-to-br from-amber-50/30 to-white border-2 border-amber-200 rounded-xl p-5">
                  <h3 className="text-lg font-semibold text-gray-950 mb-4 flex items-center gap-2">
                    <span className="text-xl">📋</span>
                    Create Order
                  </h3>

                  <div className="space-y-4">
                    {loyaltyBreakdowns.map((b, i) => (
                      <div
                        key={i}
                        className="bg-white border-2 border-gray-200 rounded-lg p-5 hover:border-amber-300 transition"
                      >
                        <div className="flex justify-between items-start mb-4">
                          <span className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm font-semibold">
                            Order #{i + 1}
                          </span>
                          {loyaltyBreakdowns.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeBreakdown(i)}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50 p-2 rounded-lg transition"
                            >
                              <span className="text-xl font-bold">×</span>
                            </button>
                          )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {/* QR Quantity */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              QR Quantity <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="number"
                              placeholder="Enter QR Quantity"
                              value={b.quantity || ""}
                              onChange={(e) =>
                                handleBreakdownChange(i, "quantity", e.target.value)
                              }
                              onWheel={(e) => e.target.blur()} 
                              className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-amber-500 transition"
                              min="1"
                            />
                          </div>

                          {/* Scheme dropdown — only shown if more than 1 scheme */}
                          {offerSchemes.length > 1 && (
                            <div className="relative">
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Scheme Name <span className="text-red-500">*</span>
                              </label>
                              <select
                                required
                                value={b.scheme || ""}
                                onChange={(e) =>
                                  handleBreakdownChange(i, "scheme", e.target.value)
                                }
                                className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg pr-10 focus:outline-none focus:border-amber-500 transition appearance-none bg-white"
                              >
                                <option value="">Select Scheme Name</option>
                                {loadingSchemes ? (
                                  <option disabled>Loading schemes...</option>
                                ) : (
                                  offerSchemes.map((scheme) => (
                                    <option key={scheme.id} value={scheme.name}>
                                      {scheme.name}
                                    </option>
                                  ))
                                )}
                              </select>
                              <div className="absolute right-3 bottom-3 pointer-events-none">
                                <svg
                                  className="w-5 h-5 text-gray-400"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M19 9l-7 7-7-7"
                                  />
                                </svg>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Gift Allocation Info */}
                {isAutoActivate && (
                  <div className="bg-gradient-to-br from-blue-50/30 to-white border-2 border-blue-200 rounded-xl p-5">
                    <h3 className="text-lg font-semibold text-gray-950 mb-3 flex items-center gap-2">
                      <span className="text-xl">ℹ️</span>
                      Gift Allocation Information
                    </h3>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <p className="text-sm text-gray-700 leading-relaxed">
                        <strong className="text-blue-700">Automatic Gift Allocation:</strong>{" "}
                        When you submit this order, the system will automatically fetch all
                        gifts associated with each scheme name from the Gift Masters and
                        create/update gift allotments accordingly. You don't need to manually
                        select gifts — they will be automatically mapped based on the scheme
                        names you've selected.
                      </p>
                    </div>
                  </div>
                )}

                {/* Review Summary — loyalty */}
                {isReviewReady && (
                  <div className="bg-gradient-to-br from-amber-50 to-white border-2 border-amber-500 p-5 rounded-xl shadow-lg">
                    <h3 className="text-base font-bold mb-4 text-gray-950 flex items-center gap-2">
                      <span className="text-lg">📊</span>
                      Review Order Summary
                    </h3>
                    <div className="overflow-x-auto">
                      <table className="w-full bg-white border border-amber-500 rounded-lg text-sm">
                        <thead>
                          <tr className="bg-gradient-to-r from-amber-400 to-amber-500 text-white text-left font-semibold">
                            <th className="py-2 px-3 border-b border-amber-600">Scheme</th>
                            <th className="py-2 px-3 border-b border-amber-600">Quantity</th>
                            <th className="py-2 px-3 border-b border-amber-600">QR Count</th>
                          </tr>
                        </thead>
                        <tbody>
                          {loyaltyBreakdowns
                            .filter((b) => b.quantity > 0)
                            .map((b, i) => (
                              <tr
                                key={i}
                                className="text-gray-800 hover:bg-amber-50 transition-colors"
                              >
                                <td className="py-2 px-3 border-b border-amber-200">
                                  {b.scheme || "-"}
                                </td>
                                <td className="py-2 px-3 border-b border-amber-200">
                                  {b.quantity}
                                </td>
                                <td className="py-2 px-3 border-b border-amber-200">
                                  {b.quantity}
                                </td>
                              </tr>
                            ))}
                        </tbody>
                        <tfoot>
                          <tr className="bg-gradient-to-r from-amber-500 to-amber-600 font-bold text-white">
                            <td className="py-2 px-3 border-t border-amber-700">Total</td>
                            <td className="py-2 px-3 border-t border-amber-700">{qrQuantity}</td>
                            <td className="py-2 px-3 border-t border-amber-700">{qrQuantity}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                )}

                {/* Action buttons */}
                <div className="flex justify-end gap-4 mt-4 pt-3 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => navigate(-1)}
                    className="bg-white border-2 border-red-500 text-red-600 py-2.5 px-6 rounded-lg hover:bg-red-50 font-semibold transition-all duration-200 shadow-sm hover:shadow-md"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting || orderLoading}
                    className="bg-gradient-to-r from-amber-500 to-amber-600 text-white py-2.5 px-6 rounded-lg hover:from-amber-600 hover:to-amber-700 font-semibold transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                  >
                    {isSubmitting || orderLoading
                      ? "Creating Order..."
                      : "Submit Loyalty Coupon"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default OrderRequest;